import React from "react";
import { redirectToStripeIntermediatePage, redirectToTicketForm } from "../util";
import classNames from "classnames";
import Icon from "assets/icons";
import { useCopies } from "shared/hooks/useCopies";
import useNavigateWrapper from "shared/hooks/useNavigateWrapper";
import { useSearchParams } from "react-router-dom";
import Button from "shared/components/button";
import ButtonType from "shared/enums/ButtonTypes";
import Variant from "shared/enums/Variant";
import { useRegDetail } from "shared/context/regDetail";
import { StripeRedirectHandlerProps } from "./type";
import QueryParamKey from "shared/enums/QueryParamKey";
import { RegistrationRoute } from "apps/registration/routes/route";
import RegistrationFooter from "apps/registration/components/registrationFooter";

export default function RegistrationStripeRedirect({
  isStripeRedirectStatusSuccess,
  makePayment,
  makePaymentError,
  displayLoader
}: StripeRedirectHandlerProps) {
  const copies = useCopies();
  const { navigate } = useNavigateWrapper();
  const [searchParams] = useSearchParams();
  const { helpdeskEmail } = useRegDetail();

  const paymentSuccessCallback = React.useCallback(
    (searchParams: URLSearchParams) => {
      const isInvoicePayer = Boolean(searchParams.get(QueryParamKey.INVOICE_PAYER_ID));
      navigate(
        {
          pathname: isInvoicePayer ? RegistrationRoute.InvoicePayerConfirmation : RegistrationRoute.Confirmation,
          search: `?${searchParams.toString()}`
        },
        { replace: true }
      );
    },
    [navigate]
  );

  React.useEffect(() => {
    if (isStripeRedirectStatusSuccess) {
      // if stripe payment was success then call api method directly
      redirectToStripeIntermediatePage(searchParams, makePayment, paymentSuccessCallback);
    }
  }, [isStripeRedirectStatusSuccess, makePayment, paymentSuccessCallback, searchParams]);

  return (
    <React.Fragment>
      <div className="container">
        <div className="stripeSuccess">
          <div className="stripeSuccess_wrapper">
            <div className="stripeSuccess_container">
              <div className="stripeSuccess_msg">
                <div
                  className={classNames("stripeSuccess_msg--icon", {
                    stripeFailedMsg: !isStripeRedirectStatusSuccess
                  })}>
                  {isStripeRedirectStatusSuccess ? <Icon.CheckMarkOutlineSlim /> : <Icon.ExclamationOutlineSlim />}
                </div>
                <p className="stripeSuccess_msg--text">
                  {isStripeRedirectStatusSuccess
                    ? copies.get("stripe.redirect.payment.successful")
                    : copies.get("stripe.redirect.payment.failed.status")}
                </p>
              </div>
              {isStripeRedirectStatusSuccess ? (
                displayLoader ? (
                  <React.Fragment>
                    <p className="stripeSuccess_msg--content">
                      {copies.get("stripe.redirect.fetching.registration.details")}
                    </p>
                    <div className="stripeSuccess_btnWrapper">
                      <Icon.LoaderCirculer className="loaderIcon" />
                    </div>
                  </React.Fragment>
                ) : makePaymentError ? (
                  <React.Fragment>
                    <p className="stripeSuccess_msg--content">
                      {copies.get("stripe.redirect.fetching.registration.details.failed")}
                    </p>
                    <div className="stripeSuccess_btnWrapper">
                      <Button
                        variant={Variant.PRIMARY}
                        label={copies.get("retry")}
                        isOutline
                        type={ButtonType.BUTTON}
                        onClick={() =>
                          redirectToStripeIntermediatePage(searchParams, makePayment, paymentSuccessCallback)
                        }
                      />
                    </div>
                    <p
                      className="stripeSuccess_helpText"
                      dangerouslySetInnerHTML={{
                        __html: copies.get("stripe.redirect.email.support", {
                          0: `<a href="mailto:${helpdeskEmail}">${helpdeskEmail}</a>`
                        })
                      }}
                    />
                  </React.Fragment>
                ) : null
              ) : (
                <React.Fragment>
                  <p className="stripeSuccess_msg--content">{copies.get("stripe.redirect.payment.failed.msg")}</p>
                  <div className="stripeSuccess_btnWrapper">
                    <Button
                      variant={Variant.PRIMARY}
                      isOutline
                      type={ButtonType.BUTTON}
                      label={copies.get("stripe.redirect.payment.failed.resume.reg")}
                      onClick={() => redirectToTicketForm(navigate, searchParams)}
                    />
                  </div>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      </div>
      <RegistrationFooter />
    </React.Fragment>
  );
}
